import { motion } from "framer-motion";
import { useEffect, useMemo } from "react";

/**
 * LoginTransition — full-screen "portal" hero shown right after a
 * successful login. Displays the HigherVision wordmark, tagline, a
 * shimmering gold progress bar, and a randomized inspirational message
 * for ~7 seconds, then fires `onComplete`.
 */

const MESSAGES = [
  "Your next opportunity could be today.",
  "One connection can change your future.",
  "Big dreams begin with small actions.",
  "Someone needs exactly what you offer.",
  "Create more than you consume today.",
  "Start where you are. Progress beats perfection.",
  "Build something people remember.",
];

export default function LoginTransition({ userName, onComplete, durationMs = 7000 }) {
  // Freeze one random message per mount so it doesn't churn on re-renders.
  const message = useMemo(
    () => MESSAGES[Math.floor(Math.random() * MESSAGES.length)],
    []
  );

  useEffect(() => {
    const t = setTimeout(() => onComplete?.(), durationMs);
    return () => clearTimeout(t);
  }, [onComplete, durationMs]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="login-transition"
      className="fixed inset-0 z-[200] flex items-center justify-center overflow-hidden"
      style={{
        background:
          "radial-gradient(circle at 30% 0%, rgba(255,215,0,0.22) 0%, transparent 55%)," +
          "radial-gradient(circle at 80% 100%, rgba(212,175,55,0.12) 0%, transparent 50%)," +
          "linear-gradient(180deg, #0a0703 0%, #050300 100%)",
      }}>
      {/* Ambient diamond specks */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 24 }).map((_, i) => (
          <motion.span
            key={i}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: [0, 1, 0], scale: [0, 1, 0] }}
            transition={{
              delay: i * 0.08, duration: 1.8,
              repeat: Infinity, repeatDelay: 0.6,
            }}
            className="absolute w-1.5 h-1.5 rotate-45"
            style={{
              top: `${(i * 37) % 100}%`,
              left: `${(i * 53) % 100}%`,
              background: "#FFD700",
              boxShadow: "0 0 12px #FFD700",
            }}
          />
        ))}
      </div>

      <div className="relative text-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="text-[10px] uppercase tracking-[0.5em] text-[#F2DC8C]/60 mb-3">
          Command Center
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 14, letterSpacing: "0.5em" }}
          animate={{ opacity: 1, y: 0, letterSpacing: "0.04em" }}
          transition={{ delay: 0.2, duration: 0.9, ease: "easeOut" }}
          className="font-display text-[52px] sm:text-[68px] leading-none"
          style={{
            background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 45%, #8a6a00 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            textShadow: "0 0 32px rgba(255,215,0,0.35)",
          }}>
          HigherVision
        </motion.h1>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-3 text-[13px] uppercase tracking-[0.4em] text-[#F2DC8C]/85">
          Connect · Build · Grow
        </motion.div>

        {userName && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.4 }}
            className="mt-8 text-[15px] text-[#F2DC8C]/80 font-display"
            data-testid="login-transition-welcome">
            Welcome back, <span className="text-[#FFD700]">{userName.split(" ")[0]}</span>.
          </motion.div>
        )}

        {/* Gold shimmer progress bar */}
        <div className="mt-8 mx-auto w-64 h-[3px] rounded-full overflow-hidden"
             style={{ background: "rgba(212,175,55,0.15)" }}>
          <motion.div
            initial={{ x: "-100%" }} animate={{ x: "0%" }}
            transition={{ duration: (durationMs - 200) / 1000, ease: "easeInOut" }}
            className="h-full w-full"
            style={{
              background: "linear-gradient(90deg, transparent, #FFF3AE 30%, #FFD700 50%, #D4AF37 70%, transparent)",
              boxShadow: "0 0 12px rgba(255,215,0,0.7)",
            }}/>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.6 }}
          className="mt-4 text-[12px] italic text-[#F2DC8C]/75 font-display max-w-md mx-auto"
          data-testid="login-transition-message">
          “{message}”
        </motion.div>
      </div>
    </motion.div>
  );
}
